import React, { useRef, useCallback, useState } from 'react';
import {
  FileText,
  Upload,
  Brain,
  XCircle,
  Shield,
  FileSpreadsheet,
  Loader2,
  Sparkles,
  FileCheck,
  Zap,
  Target,
  BarChart3,
  FileType,
  X,
} from 'lucide-react';
import { Stepper } from '../../../../../components/ui/stepper';
import StatsDashboard from './StatsDashboard';
import type { ParserPhase, ParsingMethod, ProcessingStats, Step } from '../types';

interface UploadPhaseProps {
  currentPhase: ParserPhase;
  currentStepIndex: number;
  steps: Step[];
  selectedFile: File | null;
  error: string | null;
  parsingMethod: ParsingMethod;
  aiDescription: string;
  stats: ProcessingStats | null;
  statsLoading: boolean;
  setParsingMethod: (method: ParsingMethod) => void;
  setSelectedFile: (file: File | null) => void;
  setAiDescription: (desc: string) => void;
  setCurrentPhase: (phase: ParserPhase) => void;
  setError: (error: string | null) => void;
  handleFileSelect: (file: File) => void;
  classifyDocument: () => void;
}

export default function UploadPhase({
  currentPhase,
  currentStepIndex,
  steps,
  selectedFile,
  error,
  parsingMethod,
  aiDescription,
  stats,
  statsLoading,
  setParsingMethod,
  setSelectedFile,
  setAiDescription,
  setCurrentPhase,
  setError,
  handleFileSelect,
  classifyDocument,
}: UploadPhaseProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && files[0]) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && files[0]) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Clicking "Upload Document" immediately opens the file picker
  const handleUploadClick = useCallback(() => {
    setParsingMethod('traditional');
    // Small delay so the hidden input is associated with the right method
    setTimeout(() => fileInputRef.current?.click(), 0);
  }, [setParsingMethod]);

  const showingAiMode = parsingMethod === 'ai';
  const hasFile = !!selectedFile;

  return (
    <div className="p-6 space-y-6">
      {/* Hidden file input — always in DOM */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.txt"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="p-2 sm:p-2.5 bg-blue-600 rounded-lg shrink-0">
            <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Compliance Parser</h1>
            <p className="text-slate-500 text-xs sm:text-sm mt-0.5">
              Transform compliance documents into structured templates powered by Vastabloc
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-10 sm:ml-0">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
            Systems Online
          </span>
          <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
            <Shield className="w-3 h-3" />
            Vastabloc Secure
          </span>
        </div>
      </div>

      {/* Stepper */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <Stepper steps={steps} currentStep={currentStepIndex} size="md" showSpinner={false} />
      </div>

      {/* Method Cards / Inline Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Upload Document Card */}
        {hasFile ? (
          <div
            className={`relative bg-white rounded-xl border p-6 transition-all ${
              isDragOver ? 'border-blue-400 bg-blue-50/40' : 'border-slate-200'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Ready to analyze header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Ready to analyze</span>
              </div>
              <button
                onClick={() => { setSelectedFile(null); setError(null); setParsingMethod(null); }}
                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Remove file"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* File info row */}
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg mb-4">
              <div className="w-11 h-11 bg-white border border-slate-200 rounded-lg flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{selectedFile!.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-slate-500">
                    {(selectedFile!.size / 1024 / 1024).toFixed(1)} MB
                  </span>
                  <span className="w-1 h-1 bg-slate-300 rounded-full" />
                  <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                    {selectedFile!.name.split('.').pop()?.toUpperCase() || 'FILE'}
                  </span>
                </div>
              </div>
              <FileCheck className="w-5 h-5 text-emerald-500 shrink-0" />
            </div>

            {/* Action */}
            <button
              onClick={classifyDocument}
              disabled={currentPhase === 'classifying'}
              className="w-full px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {currentPhase === 'classifying' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing Document...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4" />
                  Start AI Analysis
                </>
              )}
            </button>

            {/* Subtle footer info */}
            <div className="flex items-center justify-center gap-4 mt-3 text-[11px] text-slate-400">
              <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> End-to-end encrypted</span>
              <span className="flex items-center gap-1"><Brain className="w-3 h-3" /> Vastabloc Intelligence Engine</span>
            </div>

            {error && (
              <div className="flex items-center gap-2 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <XCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}
          </div>
        ) : (
          <button
            className={`group text-left bg-white rounded-xl border p-8 transition-all relative overflow-hidden ${
              isDragOver
                ? 'border-blue-400 bg-blue-50/60'
                : 'border-slate-200 hover:border-blue-300 hover:shadow-md'
            }`}
            onClick={handleUploadClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="relative">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-5 group-hover:bg-blue-200 transition-colors">
                <Upload className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Upload Document</h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-5">
                Upload a PDF, Word, Excel, or CSV file. Our pipeline extracts, classifies, and structures the complete framework hierarchy.
              </p>
              <div className="flex flex-wrap gap-2">
                {['PDF', 'DOCX', 'XLSX', 'CSV', 'TXT', 'PNG'].map((fmt) => (
                  <span key={fmt} className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-md">
                    {fmt}
                  </span>
                ))}
              </div>
            </div>
          </button>
        )}

        {/* Describe Standard Card */}
        {showingAiMode ? (
          <div className="bg-white rounded-xl border border-indigo-300 p-6 transition-all relative overflow-hidden">
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Describe Standard</h3>
                </div>
                <button
                  onClick={() => { setParsingMethod(null); setAiDescription(''); setError(null); }}
                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <textarea
                value={aiDescription}
                onChange={(e) => setAiDescription(e.target.value)}
                placeholder="e.g. ISO 27001 information security management system covering access control, cryptography, physical security..."
                className="w-full p-3 border border-slate-200 rounded-lg resize-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all text-sm leading-relaxed text-slate-700 placeholder:text-slate-400"
                rows={3}
                autoFocus
              />

              <div className="flex flex-wrap gap-1.5 mt-3 mb-4">
                <span className="text-[10px] text-slate-400">Try:</span>
                {['ISO 27001', 'PCI DSS v4', 'SOC 2 Type II', 'NIST CSF', 'GDPR'].map((example) => (
                  <button
                    key={example}
                    onClick={() => setAiDescription(example)}
                    className="text-[11px] text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2 py-0.5 rounded-md transition-colors"
                  >
                    {example}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPhase('ai_describe')}
                disabled={!aiDescription.trim()}
                className="w-full px-4 py-2.5 bg-indigo-600 text-white font-medium text-sm rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
                <Sparkles className="w-4 h-4" />
                Generate Framework
              </button>

              {error && (
                <div className="flex items-center gap-2 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <XCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}
            </div>
          </div>
        ) : (
          <button
            className="group text-left bg-white rounded-xl border border-slate-200 p-8 hover:border-indigo-300 hover:shadow-md transition-all relative overflow-hidden"
            onClick={() => setParsingMethod('ai')}
          >
            <div className="absolute top-4 right-4">
              <span className="text-[10px] font-bold text-white bg-indigo-600 px-2.5 py-1 rounded-full">
                AI
              </span>
            </div>
            <div className="relative">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-5 group-hover:bg-indigo-200 transition-colors">
                <Sparkles className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Describe Standard</h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-5">
                Describe the compliance standard you need in plain language. Our system generates the complete framework structure for you.
              </p>
              <div className="flex flex-wrap gap-2">
                {['ISO', 'NIST', 'PCI DSS', 'SOC 2', 'GDPR', 'Custom'].map((fmt) => (
                  <span key={fmt} className="text-xs font-medium text-indigo-500 bg-indigo-50 px-2.5 py-1 rounded-md">
                    {fmt}
                  </span>
                ))}
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Capabilities Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4">
          <div className="p-2 bg-blue-50 rounded-lg shrink-0">
            <Target className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">Auto-Detect</p>
            <p className="text-xs text-slate-500">Framework type</p>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4">
          <div className="p-2 bg-emerald-50 rounded-lg shrink-0">
            <Zap className="w-4 h-4 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">Fast Processing</p>
            <p className="text-xs text-slate-500">Under 60 seconds</p>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4">
          <div className="p-2 bg-purple-50 rounded-lg shrink-0">
            <BarChart3 className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">99.8% Accuracy</p>
            <p className="text-xs text-slate-500">Classification</p>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4">
          <div className="p-2 bg-amber-50 rounded-lg shrink-0">
            <FileType className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">Multi-Format</p>
            <p className="text-xs text-slate-500">6 file types</p>
          </div>
        </div>
      </div>

      {/* Stats Dashboard */}
      <StatsDashboard stats={stats} statsLoading={statsLoading} />
    </div>
  );
}
